/**
 * Indication Enum
 *
 * Values synced with backend IndicationEnum.
 * @see backend/src/backend/db/constants/indication.py
 *
 * Backend values:
 * - MULTIPLE_MYELOMA_1ST_LINE_ELIGIBLE = 1
 * - MULTIPLE_MYELOMA_MAINTENANCE = 2
 * - MULTIPLE_MYELOMA_1ST_LINE_INELIGIBLE = 3
 * - MULTIPLE_MYELOMA_RELAPSED_REFRACTORY = 4
 * - SMD_5Q_DELETION = 5
 * - LYMPHOMA_RELAPSED_REFRACTORY = 6
 * - OTHER = 7
 */
import { intl } from "dmsbasereact";

export const IndicationEnum = {
    MULTIPLE_MYELOMA_1ST_LINE_ELIGIBLE: 1,
    MULTIPLE_MYELOMA_MAINTENANCE: 2,
    MULTIPLE_MYELOMA_1ST_LINE_INELIGIBLE: 3,
    MULTIPLE_MYELOMA_RELAPSED_REFRACTORY: 4,
    SMD_5Q_DELETION: 5,
    LYMPHOMA_RELAPSED_REFRACTORY: 6,
    OTHER: 7,
};

/**
 * Get indication text by code
 * @param {number} code - Indication enum value
 * @returns {string} Translated indication text
 */
export const getIndicationText = (code) => {
    switch (code) {
        case IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_ELIGIBLE:
            return intl.get("Indication.MultipleMyeloma1stLineEligible").d("Mieloma Múltiplo 1ª Linha Elegível");
        case IndicationEnum.MULTIPLE_MYELOMA_MAINTENANCE:
            return intl.get("Indication.MultipleMyelomaMaintenance").d("Mieloma Múltiplo Manutenção");
        case IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_INELIGIBLE:
            return intl.get("Indication.MultipleMyeloma1stLineIneligible").d("Mieloma Múltiplo 1ª Linha Inelegível");
        case IndicationEnum.MULTIPLE_MYELOMA_RELAPSED_REFRACTORY:
            return intl.get("Indication.MultipleMyelomaRelapsedRefractory").d("Mieloma Múltiplo Recidivado/Refratário");
        case IndicationEnum.SMD_5Q_DELETION:
            return intl.get("Indication.SMD5qDeletion").d("SMD com deleção 5q");
        case IndicationEnum.LYMPHOMA_RELAPSED_REFRACTORY:
            return intl.get("Indication.LymphomaRelapsedRefractory").d("Linfoma Recidivado/Refratário");
        case IndicationEnum.OTHER:
            return intl.get("Indication.Other").d("Outro");
        default:
            return intl.get("Unknown").d("Unknown");
    }
};

/**
 * Get short indication text by code (for use in tables)
 * @param {number|string} code - Indication enum value or string
 * @returns {string} Translated short indication text
 */
export const getIndicationShortText = (code) => {
    if (typeof code === 'string' && isNaN(code)) {
        return code;
    }

    const numCode = typeof code === 'string' ? parseInt(code) : code;

    switch (numCode) {
        case IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_ELIGIBLE:
            return intl.get("Indication.MM1stLineEligibleShort").d("MM 1ª Linha Elegível");
        case IndicationEnum.MULTIPLE_MYELOMA_MAINTENANCE:
            return intl.get("Indication.MMMaintenanceShort").d("MM Manutenção");
        case IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_INELIGIBLE:
            return intl.get("Indication.MM1stLineIneligibleShort").d("MM 1ª Linha Inelegível");
        case IndicationEnum.MULTIPLE_MYELOMA_RELAPSED_REFRACTORY:
            return intl.get("Indication.MMRelapsedRefractoryShort").d("MM Recidivado/Refratário");
        case IndicationEnum.SMD_5Q_DELETION:
            return intl.get("Indication.SMD5qShort").d("SMD 5q");
        case IndicationEnum.LYMPHOMA_RELAPSED_REFRACTORY:
            return intl.get("Indication.LymphomaRelapsedRefractoryShort").d("Linfoma Recidivado/Refratário");
        case IndicationEnum.OTHER:
            return intl.get("Indication.OtherShort").d("Outro");
        default:
            return intl.get("Unknown").d("Unknown");
    }
};

/**
 * Get indication options for select components
 * @returns {Array<{value: number, label: string}>} Array of {value, label} objects
 */
export const getIndicationOptions = () => [
    {
        value: IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_ELIGIBLE,
        label: getIndicationText(IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_ELIGIBLE)
    },
    {
        value: IndicationEnum.MULTIPLE_MYELOMA_MAINTENANCE,
        label: getIndicationText(IndicationEnum.MULTIPLE_MYELOMA_MAINTENANCE)
    },
    {
        value: IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_INELIGIBLE,
        label: getIndicationText(IndicationEnum.MULTIPLE_MYELOMA_1ST_LINE_INELIGIBLE)
    },
    {
        value: IndicationEnum.MULTIPLE_MYELOMA_RELAPSED_REFRACTORY,
        label: getIndicationText(IndicationEnum.MULTIPLE_MYELOMA_RELAPSED_REFRACTORY)
    },
    {
        value: IndicationEnum.SMD_5Q_DELETION,
        label: getIndicationText(IndicationEnum.SMD_5Q_DELETION)
    },
    {
        value: IndicationEnum.LYMPHOMA_RELAPSED_REFRACTORY,
        label: getIndicationText(IndicationEnum.LYMPHOMA_RELAPSED_REFRACTORY)
    },
    {
        value: IndicationEnum.OTHER,
        label: getIndicationText(IndicationEnum.OTHER)
    }
];
